import { inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { BUILD_TIME_ENVIRONMENT } from '@core/config/app-config.tokens';
import type { AppConfig, RuntimeAppConfig } from '@core/config/app-config.types';

@Injectable({ providedIn: 'root' })
export class AppConfigService {
  private readonly environment = inject(BUILD_TIME_ENVIRONMENT);
  private readonly title = inject(Title);

  private loadedConfig?: AppConfig;

  public async load(): Promise<void> {
    const response = await fetch(this.environment.runtimeConfigUrl, {
      headers: {
        Accept: 'application/json',
      },
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(
        `Failed to load runtime config from ${this.environment.runtimeConfigUrl} (status ${response.status})`,
      );
    }

    const json = (await response.json()) as unknown;
    const runtimeConfig = this.parseRuntimeConfig(json);

    this.loadedConfig = {
      ...runtimeConfig,
      production: this.environment.production,
    };

    this.title.setTitle(this.loadedConfig.appName);
  }

  public get config(): AppConfig {
    if (!this.loadedConfig) {
      throw new Error('Application config was requested before initialization completed.');
    }

    return this.loadedConfig;
  }

  private parseRuntimeConfig(value: unknown): RuntimeAppConfig {
    if (!this.isRecord(value)) {
      throw new Error('Runtime config must be an object.');
    }

    const { apiBaseUrl, requestTimeoutMs, appName, featureFlags } = value;

    if (typeof apiBaseUrl !== 'string' || apiBaseUrl.trim().length === 0) {
      throw new Error('Runtime config "apiBaseUrl" must be a non-empty string.');
    }

    if (typeof requestTimeoutMs !== 'number' || !Number.isFinite(requestTimeoutMs)) {
      throw new Error('Runtime config "requestTimeoutMs" must be a valid number.');
    }

    if (typeof appName !== 'string' || appName.trim().length === 0) {
      throw new Error('Runtime config "appName" must be a non-empty string.');
    }

    if (!this.isBooleanRecord(featureFlags)) {
      throw new Error('Runtime config "featureFlags" must be an object with boolean values.');
    }

    return {
      apiBaseUrl,
      requestTimeoutMs,
      appName,
      featureFlags,
    };
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }

  private isBooleanRecord(value: unknown): value is Record<string, boolean> {
    if (!this.isRecord(value)) {
      return false;
    }

    return Object.values(value).every((entry) => typeof entry === 'boolean');
  }
}
